import { Avatar, Box, ButtonBase } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SportsRugbyIcon from '@mui/icons-material/SportsRugby';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { getTotalTimer } from '../live/sports/rules.js';

const DISCIPLINE_LABELS = {
  rugby: 'RUGBY',
  soccer: 'FOOTBALL',
};

const FORCE_ENDED_AFTER_MS = 24 * 60 * 60 * 1000;
const NEUTRAL_MATCH_STATUSES = new Set([
  '',
  'scheduled',
  'not_started',
  'notStarted',
  'pending',
]);

const hasValidScore = (match) =>
  typeof match?.homeScore === 'number' &&
  typeof match?.awayScore === 'number';

const getScoreLabel = (match) => {
  if (!hasValidScore(match)) {
    return match?.statusLabel || '-';
  }

  return `${match.homeScore}-${match.awayScore}`;
};

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const parsePositiveDay = (value) => {
  if (value == null || value === '') return null;

  const rawValue = String(value).trim();
  const directNumber = Number(rawValue);

  if (Number.isInteger(directNumber) && directNumber > 0) {
    return directNumber;
  }

  const dayMatch = rawValue.match(
    /(\d+)\s*(?:e|er|ère|eme|ème)?\s*(?:journ[ée]e|journee|j\.?)/i
  );

  if (dayMatch) {
    return parsePositiveDay(dayMatch[1]);
  }

  const numbers = rawValue.match(/\d+/g);

  if (!numbers?.length) return null;

  // Ex : "2022 37E Journée" -> on veut 37, pas 2022.
  return parsePositiveDay(numbers[numbers.length - 1]);
};

const formatTimeLabel = (value) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const getMatchTimestamp = (match) => {
  const parsed = new Date(match?.dateTime || match?.time || 0);
  const timestamp = parsed.getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const normalizeUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/') || url.startsWith('http')) return url;
  return `/${url}`;
};

const isMatchStarted = (match) =>
  toNumber(match?.timer) !== 0 ||
  toNumber(match?.period) !== 0 ||
  match?.statusTimer === 'on';

const hasBlockingStatus = (match) => {
  const status = String(match?.status || '').trim();
  return Boolean(status) && !NEUTRAL_MATCH_STATUSES.has(status);
};

const isMatchPastGracePeriod = (match) => {
  const timestamp = getMatchTimestamp(match);

  if (!Number.isFinite(timestamp) || timestamp === Number.MAX_SAFE_INTEGER) {
    return false;
  }

  return Date.now() - timestamp >= FORCE_ENDED_AFTER_MS;
};

const isMatchEnded = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);
  const statusTimer = match?.statusTimer;

  if (match?.status === 'end') {
    return true;
  }

  const isNativeEnded =
    ['soccer', 'rugby'].includes(discipline) &&
    timer === 0 &&
    statusTimer === 'off' &&
    [2, 6].includes(period);

  if (isNativeEnded) {
    return true;
  }

  return !hasBlockingStatus(match) && isMatchPastGracePeriod(match);
};

const isMatchReported = (match) => match?.status === 'reported';

const isHalfTime = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);

  return (
    ['soccer', 'rugby'].includes(discipline) &&
    match?.statusTimer === 'off' &&
    timer === 0 &&
    [1, 4].includes(period)
  );
};

const isEndRegulationTime = (match) => {
  const discipline = match?.discipline;
  const timer = toNumber(match?.timer);
  const period = toNumber(match?.period);

  return (
    ['soccer', 'rugby'].includes(discipline) &&
    match?.statusTimer === 'off' &&
    timer === 0 &&
    period === 3
  );
};

const isPenaltyShootout = (match) =>
  ['soccer', 'rugby'].includes(match?.discipline) &&
  toNumber(match?.period) === 5;

const getMinuteLabel = (match) => {
  const period = toNumber(match?.period);
  const timer = Math.max(0, toNumber(match?.timer));
  const discipline = match?.discipline;

  const totalTimer = getTotalTimer(period, timer, discipline);
  const normalizedTotalTimer = Number.isFinite(Number(totalTimer))
    ? Number(totalTimer)
    : timer;

  const minutes = Math.floor(normalizedTotalTimer / 60);

  return `${minutes + 1}'`;
};

const getStatusLabel = (match) => {
  if (isMatchReported(match)) return 'Reporté';
  if (isMatchEnded(match)) return 'Fin du match';
  if (isPenaltyShootout(match)) return 't.a.b';
  if (isHalfTime(match)) return 'Mi-temps';
  if (isEndRegulationTime(match)) return 'Fin du temps réglementaire';
  if (isMatchStarted(match)) return getMinuteLabel(match);
  return formatTimeLabel(match?.dateTime || match?.time);
};

const isForfeit = (match, statusLabel = '') => {
  const rawStatus = String(match?.status || '').toLowerCase();
  const rawLabel = String(match?.statusLabel || statusLabel || '').toLowerCase();

  return rawStatus.includes('forfeit') ||
    rawStatus.includes('forfait') ||
    rawLabel.includes('forfeit') ||
    rawLabel.includes('forfait');
};

const getMatchTone = (match, statusLabel) => {
  if (isForfeit(match, statusLabel)) return 'forfeit';
  if (isMatchReported(match)) return 'reported';
  if (isHalfTime(match) || isEndRegulationTime(match)) return 'half-time';
  if (isMatchStarted(match) && match?.statusTimer === 'on') return 'live';
  if (isMatchEnded(match)) return 'ended';
  return 'scheduled';
};

const shouldShowStatusBadge = (tone) =>
  ['live', 'half-time'].includes(tone);

const shouldRenderMainAsChip = (tone, hasScore) =>
  !hasScore && ['forfeit', 'reported', 'half-time'].includes(tone);

const getCompetitionMetaLabel = (competition) => {
  const parts = [competition.season, competition.roundLabel].filter(Boolean);
  return parts.join(' ');
};

const getCompetitionId = (competition) => {
  const id =
    competition?.competitionId ||
    competition?._id ||
    competition?.id ||
    competition?.competition?._id ||
    competition?.competition?.id;

  return id ? String(id) : null;
};

const getCompetitionDay = (competition, matches) => {
  const firstMatch = Array.isArray(matches) ? matches[0] : null;

  return (
    parsePositiveDay(competition?.day) ||
    parsePositiveDay(competition?.matchDay) ||
    parsePositiveDay(competition?.matchday) ||
    parsePositiveDay(competition?.roundNumber) ||
    parsePositiveDay(competition?.round) ||
    parsePositiveDay(competition?.currentDay) ||
    parsePositiveDay(competition?.calendarDay) ||
    parsePositiveDay(competition?.roundLabel) ||
    parsePositiveDay(firstMatch?.day) ||
    parsePositiveDay(firstMatch?.matchDay) ||
    parsePositiveDay(firstMatch?.matchday) ||
    parsePositiveDay(firstMatch?.roundNumber) ||
    parsePositiveDay(firstMatch?.round)
  );
};

const buildStandingsUrl = ({ competitionId, day }) =>
  `/dsas-api/classement-competitions?competitionIds=${encodeURIComponent(
    competitionId
  )}&day=${encodeURIComponent(day)}`;

const getCompetitionStandingsParams = (competition, matches) => {
  const competitionId = getCompetitionId(competition);
  const day = getCompetitionStandingDay(competition, matches);

  if (!competitionId || day == null) return null;

  return {
    competitionId,
    day,
    competitionName: competition?.competitionName || competition?.name || '',
  };
};

const normalizeStandingDay = (value) => {
  if (value == null || value === '') return null;

  const stringValue = String(value).trim();
  if (!stringValue) return null;

  const numericValue = Number(stringValue);
  return Number.isFinite(numericValue) ? numericValue : stringValue;
};

const extractDayFromRoundLabel = (roundLabel) => {
  const label = String(roundLabel || '').trim();
  if (!label) return null;

  // Ex: "2022 37E Journée" => 37
  const dayMatch = label.match(/\b(\d+)\s*(?:e|ère|eme|ème)?\s*journée\b/i);
  if (dayMatch?.[1]) {
    return Number(dayMatch[1]);
  }

  // Ex: "Quarts de finale", "Demi-finale", "Finale"
  return label;
};

const getCompetitionStandingDay = (competition, matches = []) => {
  const directValue =
    competition?.day ??
    competition?.matchDay ??
    competition?.roundNumber ??
    competition?.roundType ??
    competition?.phase ??
    competition?.roundLabel;

  const normalizedDirectValue =
    directValue === competition?.roundLabel
      ? extractDayFromRoundLabel(directValue)
      : normalizeStandingDay(directValue);

  if (normalizedDirectValue != null) {
    return normalizedDirectValue;
  }

  const firstMatch = matches[0];

  return normalizeStandingDay(
    firstMatch?.day ??
      firstMatch?.matchDay ??
      firstMatch?.roundNumber ??
      firstMatch?.roundType ??
      firstMatch?.phase
  );
};


export default function DirectCalendarSection({
  group,
  showDisciplineTitle = true,
  onNavigate,
  onStandingsClick,
}) {
  const competitions = Array.isArray(group?.competitions)
    ? group.competitions
    : [];

  const discipline = group?.discipline;

  return (
    <Box className="dc-calendar-section">
      {showDisciplineTitle ? (
        <h1 className="dc-calendar-section__discipline-title">
          {DISCIPLINE_LABELS[discipline] || group?.label || discipline}
        </h1>
      ) : null}

      <Box className="dc-calendar-section__competitions">
        {competitions.map((competition) => {
          const matches = Array.isArray(competition.matches)
            ? [...competition.matches].sort(
                (a, b) => getMatchTimestamp(a) - getMatchTimestamp(b)
              )
            : [];
          const metaLabel = getCompetitionMetaLabel(competition);
          const standingsParams = getCompetitionStandingsParams(competition, matches);

          return (
            <Box
              key={competition.competitionId || competition.competitionName}
              className="dc-competition"
            >
              <Box className="dc-competition__header">
                <Box>
                  <h2 className="dc-competition__title">
                    {competition.competitionName}
                  </h2>

                  {metaLabel ? (
                    <p className="dc-competition__meta">
                      <span className="dc-competition__meta-dash">—</span>
                      <span>{metaLabel}</span>
                    </p>
                  ) : null}
                </Box>

                {standingsParams ? (
                  <Box className="dc-competition__standings-container">
                  <ButtonBase
                    className="dc-competition__standings"
                    onClick={() => {
                      if (onStandingsClick) {
                        onStandingsClick(standingsParams);
                        return;
                      }

                      onNavigate?.(buildStandingsUrl(standingsParams));
                    }}
                  >
                    <span>Classement</span>
                    <ChevronRightIcon className="dc-competition__standings-icon" />
                  </ButtonBase>
                  </Box>
                ) : null}
              </Box>

              <p className="dc-competition__hint">
                Cliquer sur un match pour accéder au live
              </p>

              <Box className="dc-competition__matches">
                {matches.map((match) => {
                  const normalizedMatch = {
                    ...match,
                    discipline: match.discipline || discipline,
                  };
                  const statusLabel = getStatusLabel(normalizedMatch);
                  const tone = getMatchTone(normalizedMatch, statusLabel);
                  const matchHasScore = hasValidScore(normalizedMatch);
                  const matchUrl = normalizeUrl(match.liveUrl);

                  const rowClassName = [
                    'dc-match-row',
                    `dc-match-row--${tone}`,
                    matchUrl ? 'dc-match-row--clickable' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const homeClassName = [
                    'dc-match-team',
                    'dc-match-team--home',
                    match.homeWinner ? 'dc-match-team--winner' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const awayClassName = [
                    'dc-match-team',
                    'dc-match-team--away',
                    match.awayWinner ? 'dc-match-team--winner' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const scoreClassName = [
                    'dc-match-score',
                    tone === 'live' ? 'dc-match-score--live' : '',
                    tone === 'half-time' ? 'dc-match-score--half-time' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const mainLabelClassName = [
                    'dc-match-main-label',
                    `dc-match-main-label--${tone}`,
                    shouldRenderMainAsChip(tone, matchHasScore)
                      ? 'dc-match-main-label--chip'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <Box
                      key={match._id || match.idMatch || match.id}
                      className={rowClassName}
                      onClick={() => {
                        if (matchUrl) onNavigate?.(matchUrl);
                      }}
                    >
                      <p className={homeClassName}>
                        {match.homeTeam?.name || match.homeName}
                      </p>

                      <TeamLogo team={match.homeTeam} discipline={discipline} />

                      <Box className="dc-match-center">
                        {matchHasScore ? (
                          <>
                            <span className={scoreClassName}>
                              {getScoreLabel(normalizedMatch)}
                            </span>

                            {shouldShowStatusBadge(tone) && statusLabel ? (
                              <span className={`dc-match-status-badge dc-match-status-badge--${tone}`}>
                                {statusLabel}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className={mainLabelClassName}>
                            {statusLabel || getScoreLabel(normalizedMatch)}
                          </span>
                        )}
                      </Box>

                      <TeamLogo team={match.awayTeam} discipline={discipline} />

                      <p className={awayClassName}>
                        {match.awayTeam?.name || match.awayName}
                      </p>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function TeamLogo({ team, discipline }) {
  return (
    <Avatar
      src={team?.logoUrl || ''}
      alt={team?.name || ''}
      className="dc-match-logo"
    >
      {discipline === 'soccer' ? (
        <SportsSoccerIcon fontSize="small" />
      ) : (
        <SportsRugbyIcon fontSize="small" />
      )}
    </Avatar>
  );
}
